import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { FontIcon } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  PagedDataGrid,
  type IPagedDataGridRefType,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import { appRoles } from '../../authConfig';
import {
  getTenants,
  deleteTenant,
  ITenant,
} from '../../services/tenantService';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  successMessageAtom,
  errorMessageAtom,
} from '../../atoms/messageBarAtoms';
import ConfirmDialog from '../../common/ConfirmDialog';
import CloneTenantPanel from './CloneTenantPanel';

const Tenants = () => {
  const profileData = useAtomValue(profileDataAtom);
  const navigate = useNavigate();
  const [selectedTenantId, setSelectedTenantId] = useState<number>();
  const [selectedTenantName, setSelectedTenantName] = useState<string>();
  const pagedDataGridRef = useRef<IPagedDataGridRefType>(null);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const [
    isClonePanelOpen,
    { setTrue: showClonePanel, setFalse: hideClonePanel },
  ] = useBoolean(false);
  const [
    isDeleteConfirmOpen,
    { setTrue: showDeleteConfirm, setFalse: hideDeleteConfirm },
  ] = useBoolean(false);

  const columns: DataGridColumn<ITenant>[] = [
    { key: 'id', name: 'Id', minWidth: 50 },
    { key: 'name', name: 'Name', minWidth: 100 },
    { key: 'code', name: 'Code', minWidth: 100 },
    { key: 'description', name: 'Description', minWidth: 200 },
  ];

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: (ctx) => ctx.refresh(),
      },
      {
        key: 'newTenant',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => navigate('/newTenant'),
        disabled: !profileData.roles.includes(appRoles.Admin),
      },
      {
        key: 'editTenant',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => navigate(`/editTenant?id=${selectedTenantId}`),
        disabled:
          !profileData.roles.includes(appRoles.Admin) || !selectedTenantId,
      },
      {
        key: 'cloneTenant',
        text: 'Clone',
        icon: <FontIcon iconName="Copy" />,
        onClick: () => showClonePanel(),
        disabled:
          !profileData.roles.includes(appRoles.Admin) || !selectedTenantId,
      },
      {
        key: 'deleteTenant',
        text: 'Delete',
        icon: <FontIcon iconName="Delete" />,
        onClick: () => showDeleteConfirm(),
        disabled:
          !profileData.roles.includes(appRoles.Admin) || !selectedTenantId,
      },
    ],
    [
      profileData.roles,
      selectedTenantId,
      navigate,
      showClonePanel,
      showDeleteConfirm,
    ]
  );

  return (
    <>
      <PagedDataGrid<ITenant>
        ref={pagedDataGridRef}
        columns={columns}
        getAction={getTenants}
        commandBarItems={commandBarItems}
        selectionMode="single"
        onSelectionChange={(selection) => {
          setSelectedTenantId(Number(selection.id));
          setSelectedTenantName((selection as ITenant).name);
        }}
        onRowDoubleClicked={(selection) =>
          navigate(`/editTenant?id=${selection.id}`)
        }
      />
      <ConfirmDialog
        isModalOpen={isDeleteConfirmOpen}
        hideModal={hideDeleteConfirm}
        message={`The Tenant ${selectedTenantId} would be deleted, are you sure?`}
        onYesClick={() => {
          hideDeleteConfirm();
          if (!selectedTenantId) return;
          const abortController = new AbortController();
          deleteTenant(abortController, selectedTenantId)
            .then(() => {
              pagedDataGridRef.current?.refresh();
              setSuccessMessage(
                `Tenant ${selectedTenantId} successfully deleted`
              );
            })
            .catch((error) => {
              setErrorMessage(error.message);
            });
        }}
      />
      {selectedTenantId && (
        <CloneTenantPanel
          isOpen={isClonePanelOpen}
          onDismiss={hideClonePanel}
          tenantId={selectedTenantId}
          tenantName={selectedTenantName}
          onCloned={() => {
            setSuccessMessage('Tenant cloned successfully.');
            pagedDataGridRef.current?.refresh();
          }}
        />
      )}
    </>
  );
};

export default Tenants;
